// Deposits held, journals, reports and period close.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Lock, LockOpen, RotateCcw, ShieldCheck } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, RECORDS_NOTE, can, docTypeLabel, downloadFile, fmtMinor, idemHeaders, monthStartISO,
         todayISO } from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Stat, Table, inputClass } from "./shared";

// ---------------------------------------------------------------------------

export function DepositsTab() {
  const [deposits, setDeposits] = useState(null);
  const [advances, setAdvances] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, a] = await Promise.all([
          api.get("/finance/reports/deposits"), api.get("/finance/reports/advances")]);
        setDeposits(d.data);
        setAdvances(a.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loading label="Reading deposits..." />;

  const rows = (report, kind) => (
    <Table head={["Booking", "Customer", "Booking status", { key: "held", label: "Held", align: "right" },
                  "Matches booking"]}
           empty={(report?.rows || []).length === 0 && <Empty>Nothing held right now.</Empty>}>
      {(report?.rows || []).map((row) => (
        <tr key={row.booking_id} data-testid={`books-${kind}-${row.booking_reference}`}>
          <td className="py-2 pr-3 font-medium">{row.booking_reference || row.booking_id}</td>
          <td className="py-2 pr-3 text-slate-600">{row.customer_name || "-"}</td>
          <td className="py-2 pr-3 text-slate-600">
            {row.booking_status} {row.flags?.includes("booking_cancelled") &&
              <Pill status="warning">cancelled</Pill>}
          </td>
          <td className="py-2 pr-3 text-right"><Money minor={row.held_minor} /></td>
          <td className="py-2 pr-3">
            {row.matches_booking ? <Pill status="posted">matches</Pill> : <Pill status="critical">check</Pill>}
          </td>
        </tr>
      ))}
    </Table>
  );

  return (
    <div className="space-y-4" data-testid="books-deposits">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Deposits you hold" value={fmtMinor(deposits?.total_held_minor)} testId="books-deposit-total"
              hint="Money that belongs to renters" />
        <Stat label="Advances held" value={fmtMinor(advances?.total_held_minor)} testId="books-advance-total"
              hint="Paid ahead of the rental charge" />
      </div>
      <Section title="Security deposits" description="A deposit is money you owe back, never income."
               testId="books-deposit-report">
        {rows(deposits, "deposit")}
      </Section>
      <Section title="Advance payments" description="Applied to the rental charge when it is posted."
               testId="books-advance-report">
        {rows(advances, "advance")}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function JournalsTab({ status, onChanged }) {
  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const canCorrect = can(status, CAPABILITIES.correct);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/journals", { params: { limit: 100 } });
      setRegister(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openJournal = async (id) => {
    const { data } = await api.get(`/finance/journals/${id}`);
    setOpen(data);
  };

  if (loading) return <Loading label="Reading journals..." />;

  return (
    <div className="space-y-4" data-testid="books-journals">
      <Section title="Journal register" description="Every posted entry, newest first. Nothing here is ever edited."
               testId="books-journal-register">
        <Table head={["Date", "Entry", "Source", { key: "amount", label: "Amount", align: "right" }, ""]}
               empty={(register?.items || []).length === 0 && <Empty>No journals yet.</Empty>}>
          {(register?.items || []).map((journal) => (
            <tr key={journal.id} data-testid={`books-journal-${journal.id}`}>
              <td className="py-2 pr-3 text-slate-600">{journal.transaction_date}</td>
              <td className="py-2 pr-3">
                <div className="text-slate-900">{journal.description}</div>
                {journal.reverses_journal_id && <Pill status="reversed">reversal</Pill>}
                {journal.reversed_by_journal_id && <Pill status="reversed">reversed</Pill>}
              </td>
              <td className="py-2 pr-3 text-slate-600">{docTypeLabel(journal.source_type)}</td>
              <td className="py-2 pr-3 text-right"><Money minor={journal.total_minor} /></td>
              <td className="py-2 pr-3 text-right">
                <Button variant="secondary" onClick={() => openJournal(journal.id)}
                        data-testid={`books-journal-open-${journal.id}`}>View</Button>
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      {open && (
        <JournalModal detail={open} canCorrect={canCorrect} onClose={() => setOpen(null)}
                      onReversed={() => { setOpen(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}

function JournalModal({ detail, canCorrect, onClose, onReversed }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const journal = detail.journal;
  const reversible = canCorrect && journal.status === "posted" && !journal.reverses_journal_id
                     && !journal.reversed_by_journal_id;

  const reverse = async () => {
    if (reason.trim().length < 5) return toast.error("Give a reason (at least 5 characters).");
    setBusy(true);
    try {
      await api.post(`/finance/journals/${journal.id}/reverse`, { reason: reason.trim() },
                     { headers: idemHeaders() });
      toast.success("Reversal posted. The original entry stays in the books.");
      onReversed();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={journal.description} onClose={onClose} testId="books-journal-modal"
           description={`${journal.transaction_date} · ${docTypeLabel(journal.source_type)} · period ${journal.accounting_period}`}>
      <Table head={["Account", "Memo", { key: "debit", label: "Debit", align: "right" },
                    { key: "credit", label: "Credit", align: "right" }]}>
        {(detail.lines || []).map((line) => (
          <tr key={line.id}>
            <td className="py-2 pr-3">
              <span className="text-slate-500 mr-2">{line.account_code}</span>{line.account_name}
            </td>
            <td className="py-2 pr-3 text-slate-600">{line.memo || "-"}</td>
            <td className="py-2 pr-3 text-right">{line.debit_minor ? <Money minor={line.debit_minor} /> : ""}</td>
            <td className="py-2 pr-3 text-right">{line.credit_minor ? <Money minor={line.credit_minor} /> : ""}</td>
          </tr>
        ))}
      </Table>

      {detail.source_document && (
        <p className="text-sm text-slate-600">
          Source: <span className="font-medium">{detail.source_document.number}</span>{" "}
          ({docTypeLabel(detail.source_document.doc_type)})
        </p>
      )}
      {journal.original_transaction_date && (
        <p className="text-xs text-amber-700">
          Dated {journal.transaction_date} because {journal.original_transaction_date} falls in a closed period.
        </p>
      )}

      {reversible && (
        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
          <Field label="Reverse this entry" hint="A reversal is a new opposite entry; nothing is deleted.">
            <input className={inputClass} value={reason} placeholder="Why is this being corrected?"
                   data-testid="books-reverse-reason" onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Button variant="danger" onClick={reverse} busy={busy} data-testid="books-reverse-submit">
            <RotateCcw className="w-4 h-4" /> Post reversal
          </Button>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

const REPORTS = [
  { key: "trial-balance", label: "Trial balance", path: "/finance/reports/trial-balance", asOf: true },
  { key: "profit-and-loss", label: "Profit and loss", path: "/finance/reports/profit-and-loss", range: true },
  { key: "balance-sheet", label: "Balance sheet", path: "/finance/reports/balance-sheet", asOf: true },
  { key: "cash-movement", label: "Cash in and out", path: "/finance/reports/cash-movement", range: true },
  { key: "by-category", label: "Income and costs by category", path: "/finance/reports/by-category", range: true },
  { key: "vehicle-profitability", label: "Vehicle profitability", path: "/finance/reports/vehicle-profitability",
    range: true },
  { key: "ap-aging", label: "What you owe", path: "/finance/reports/ap-aging", asOf: true },
];

export function ReportsTab({ status }) {
  const [key, setKey] = useState("trial-balance");
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const report = REPORTS.find((r) => r.key === key);
  const canExport = can(status, CAPABILITIES.export);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = report.range ? { date_from: from, date_to: to } : { as_of: to };
    api.get(report.path, { params })
      .then(({ data: body }) => { if (alive) setData(body); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key, from, to]);   // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async (kind) => {
    try {
      await downloadFile(`/finance/exports/${kind}.csv?date_from=${from}&date_to=${to}`,
                         `${kind}-${from}-${to}.csv`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportPackage = async () => {
    try {
      await downloadFile(`/finance/exports/accountant-package.zip?date_from=${from}&date_to=${to}`,
                         `accountant-records-${from}-${to}.zip`);
      toast.success("Accountant-ready records downloaded.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="books-reports">
      <Section title="Reports" description={RECORDS_NOTE} testId="books-reports-controls"
               actions={canExport && (
                 <div className="flex flex-wrap gap-2">
                   <Button variant="secondary" onClick={() => exportCsv(key === "profit-and-loss"
                     ? "profit-and-loss" : key === "balance-sheet" ? "balance-sheet" : "trial-balance")}
                           data-testid="books-export-csv">
                     <FileSpreadsheet className="w-4 h-4" /> CSV
                   </Button>
                   <Button variant="secondary" onClick={() => exportCsv("journal-lines")}>
                     <Download className="w-4 h-4" /> All journal lines
                   </Button>
                   <Button onClick={exportPackage} data-testid="books-export-package">
                     Accountant package
                   </Button>
                 </div>)}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Report">
            <select className={inputClass} value={key} data-testid="books-report-select"
                    onChange={(e) => setKey(e.target.value)}>
              {REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Field>
          {report.range && (
            <Field label="From">
              <input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
          )}
          <Field label={report.range ? "To" : "As of"}>
            <input type="date" className={inputClass} value={to} data-testid="books-report-to"
                   onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title={report.label} testId={`books-report-${key}`}>
        {loading ? <Loading /> : <ReportBody reportKey={key} data={data} />}
      </Section>
    </div>
  );
}

function ReportBody({ reportKey, data }) {
  if (!data) return <Empty>No data.</Empty>;
  if (reportKey === "trial-balance") {
    return (
      <>
        <Table head={["Code", "Account", { key: "d", label: "Debit", align: "right" },
                      { key: "c", label: "Credit", align: "right" }]}>
          {(data.rows || []).map((row) => (
            <tr key={row.account_id}>
              <td className="py-2 pr-3 text-slate-500">{row.code}</td>
              <td className="py-2 pr-3">{row.name}</td>
              <td className="py-2 pr-3 text-right">
                {row.balance_debit_minor ? <Money minor={row.balance_debit_minor} /> : ""}</td>
              <td className="py-2 pr-3 text-right">
                {row.balance_credit_minor ? <Money minor={row.balance_credit_minor} /> : ""}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="py-2 pr-3" colSpan={2}>Total</td>
            <td className="py-2 pr-3 text-right"><Money minor={data.total_balance_debit_minor} /></td>
            <td className="py-2 pr-3 text-right"><Money minor={data.total_balance_credit_minor} /></td>
          </tr>
        </Table>
        <p className={`mt-2 text-sm ${data.balanced ? "text-emerald-700" : "text-rose-700"}`}
           data-testid="books-tb-balanced">
          {data.balanced ? "Debits and credits balance exactly." : "This trial balance does not balance."}
        </p>
      </>
    );
  }
  if (reportKey === "profit-and-loss" || reportKey === "by-category") {
    const sections = [["Income", data.revenue], ["Discounts and credits", data.contra_revenue],
                      ["Costs", data.expenses]];
    return (
      <>
        {sections.map(([label, rows]) => (
          <div key={label} className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1">{label}</div>
            <Table head={["Account", { key: "a", label: "Amount", align: "right" }]}
                   empty={(rows || []).length === 0 && <Empty>Nothing here.</Empty>}>
              {(rows || []).map((row) => (
                <tr key={row.account_id}>
                  <td className="py-2 pr-3"><span className="text-slate-500 mr-2">{row.code}</span>{row.name}</td>
                  <td className="py-2 pr-3 text-right"><Money minor={row.amount_minor} /></td>
                </tr>
              ))}
            </Table>
          </div>
        ))}
        <div className="rounded-xl bg-slate-50 p-3 text-sm flex justify-between" data-testid="books-pnl-total">
          <span className="font-medium">Profit for the period</span>
          <Money minor={data.net_income_minor ?? (data.net_revenue_minor - data.total_expenses_minor)} />
        </div>
      </>
    );
  }
  if (reportKey === "balance-sheet") {
    return (
      <>
        {[["What the business has", data.assets], ["What it owes", data.liabilities], ["Owner's stake", data.equity]]
          .map(([label, rows]) => (
            <div key={label} className="mb-3">
              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1">{label}</div>
              <Table head={["Account", { key: "a", label: "Amount", align: "right" }]}
                     empty={(rows || []).length === 0 && <Empty>Nothing here.</Empty>}>
                {(rows || []).map((row) => (
                  <tr key={row.account_id}>
                    <td className="py-2 pr-3"><span className="text-slate-500 mr-2">{row.code}</span>{row.name}</td>
                    <td className="py-2 pr-3 text-right"><Money minor={row.amount_minor} /></td>
                  </tr>
                ))}
              </Table>
            </div>
          ))}
        <div className="rounded-xl bg-slate-50 p-3 text-sm space-y-1">
          <div className="flex justify-between"><span>Earnings so far</span>
            <Money minor={data.earnings_to_date_minor} /></div>
          <div className="flex justify-between font-medium"><span>Total</span>
            <span><Money minor={data.total_assets_minor} /> vs{" "}
              <Money minor={data.total_liabilities_minor + data.total_equity_minor} /></span></div>
          <div className={data.balanced ? "text-emerald-700" : "text-rose-700"} data-testid="books-bs-balanced">
            {data.balanced ? "The balance sheet balances." : "The balance sheet does not balance."}
          </div>
        </div>
      </>
    );
  }
  if (reportKey === "cash-movement") {
    return (
      <Table head={["Account", { key: "o", label: "Opening", align: "right" },
                    { key: "i", label: "In", align: "right" }, { key: "u", label: "Out", align: "right" },
                    { key: "c", label: "Closing", align: "right" }]}
             empty={(data.accounts || []).length === 0 && <Empty>No cash movement.</Empty>}>
        {(data.accounts || []).map((row) => (
          <tr key={row.account_id}>
            <td className="py-2 pr-3"><span className="text-slate-500 mr-2">{row.code}</span>{row.name}</td>
            <td className="py-2 pr-3 text-right"><Money minor={row.opening_minor} /></td>
            <td className="py-2 pr-3 text-right"><Money minor={row.inflow_minor} /></td>
            <td className="py-2 pr-3 text-right"><Money minor={row.outflow_minor} /></td>
            <td className="py-2 pr-3 text-right font-medium"><Money minor={row.closing_minor} /></td>
          </tr>
        ))}
      </Table>
    );
  }
  if (reportKey === "vehicle-profitability") {
    return (
      <Table head={["Vehicle", { key: "r", label: "Income", align: "right" },
                    { key: "e", label: "Costs", align: "right" }, { key: "p", label: "Profit", align: "right" }]}
             empty={(data.rows || []).length === 0 && <Empty>No vehicle-tagged entries yet.</Empty>}>
        {(data.rows || []).map((row) => (
          <tr key={row.vehicle_id}>
            <td className="py-2 pr-3">{row.vehicle_name || row.vehicle_id}
              <div className="text-xs text-slate-500">{row.plate_number}</div></td>
            <td className="py-2 pr-3 text-right"><Money minor={row.revenue_minor} /></td>
            <td className="py-2 pr-3 text-right"><Money minor={row.expenses_minor} /></td>
            <td className="py-2 pr-3 text-right font-medium"><Money minor={row.profit_minor} /></td>
          </tr>
        ))}
      </Table>
    );
  }
  return (
    <Table head={["Document", "Due", "Who", { key: "o", label: "Open", align: "right" }]}
           empty={(data.rows || []).length === 0 && <Empty>Nothing outstanding.</Empty>}>
      {(data.rows || []).map((row) => (
        <tr key={row.document_id}>
          <td className="py-2 pr-3">{row.number}</td>
          <td className="py-2 pr-3 text-slate-600">{row.due_date || row.date}</td>
          <td className="py-2 pr-3 text-slate-600">{row.counterparty_name || row.customer_name || "-"}</td>
          <td className="py-2 pr-3 text-right"><Money minor={row.open_minor} /></td>
        </tr>
      ))}
    </Table>
  );
}

// ---------------------------------------------------------------------------

export function PeriodsTab({ status, isOwner, onChanged }) {
  const [periods, setPeriods] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const canClose = can(status, CAPABILITIES.closePeriod);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/periods");
      setPeriods(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openChecklist = async (period) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/finance/periods/${period}/checklist`);
      setChecklist(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (period, action, reason) => {
    setBusy(true);
    try {
      await api.post(`/finance/periods/${period}/${action}`, reason ? { reason } : {});
      toast.success(`Period ${period} ${action === "close" ? "closed" : action === "lock" ? "locked" : "reopened"}.`);
      setChecklist(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Reading periods..." />;

  return (
    <div className="space-y-4" data-testid="books-periods">
      <Section title="Monthly close"
               description="Closing a month freezes it. Corrections afterwards are recorded as adjustments.">
        <Table head={["Month", "Status", "Entries", ""]}>
          {periods.map((period) => (
            <tr key={period.period} data-testid={`books-period-${period.period}`}>
              <td className="py-2 pr-3 font-medium">{period.period}</td>
              <td className="py-2 pr-3"><Pill status={period.status}>{period.status}</Pill></td>
              <td className="py-2 pr-3 text-slate-600">{period.journal_count || 0}</td>
              <td className="py-2 pr-3">
                <div className="flex flex-wrap justify-end gap-1">
                  <Button variant="secondary" onClick={() => openChecklist(period.period)} busy={busy}
                          data-testid={`books-period-check-${period.period}`}>Checklist</Button>
                  {period.status === "CLOSED" && isOwner && (
                    <Button variant="secondary" data-testid={`books-period-lock-${period.period}`}
                            onClick={() => {
                              const reason = window.prompt("Why lock this month?") || "";
                              if (reason.trim()) act(period.period, "lock", reason.trim());
                            }}>
                      <Lock className="w-4 h-4" /> Lock
                    </Button>
                  )}
                  {period.status !== "OPEN" && canClose && (
                    <Button variant="secondary" data-testid={`books-period-reopen-${period.period}`}
                            onClick={() => {
                              const reason = window.prompt("Why reopen this month?") || "";
                              if (reason.trim().length >= 5) act(period.period, "reopen", reason.trim());
                            }}>
                      <LockOpen className="w-4 h-4" /> Reopen
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      {checklist && (
        <Modal title={`Close checklist - ${checklist.period}`} onClose={() => setChecklist(null)}
               testId="books-checklist-modal">
          <div className="space-y-2">
            {checklist.items.map((item) => (
              <div key={item.code} className={`rounded-xl border p-3 text-sm ${
                item.ok ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
                   data-testid={`books-checklist-${item.code}`}>
                <span className="font-medium">{item.label}</span>
                {!item.ok && item.details?.length > 0 && (
                  <div className="text-xs text-slate-600 mt-1">{item.details.join(", ")}</div>
                )}
              </div>
            ))}
            {(checklist.warnings || []).filter((w) => w.count > 0).map((w) => (
              <div key={w.code} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm">
                {w.label}: <span className="font-medium">{w.count}</span>
              </div>
            ))}
          </div>
          {checklist.status === "OPEN" && canClose && (
            <Button onClick={() => act(checklist.period, "close")} busy={busy}
                    disabled={!checklist.ready_to_close} data-testid="books-close-period">
              <ShieldCheck className="w-4 h-4" /> Close {checklist.period}
            </Button>
          )}
        </Modal>
      )}
    </div>
  );
}
